import { useState, useEffect } from 'react';
import { databaseService } from '../../services/database';
import {
  Configuration,
  Experience,
  ExperienceRelease,
  ConfigurationRelease,
} from '../../types/database';

interface AssignReleaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  configurations: Configuration[];
  experiences: Experience[];
  initialConfigId?: string;
  initialPhase?: string;
  existingRelease?: ConfigurationRelease;
}

export default function AssignReleaseDialog({
  isOpen,
  onClose,
  onSave,
  configurations,
  experiences,
  initialConfigId,
  initialPhase,
  existingRelease,
}: AssignReleaseDialogProps) {
  const [configId, setConfigId] = useState(initialConfigId || '');
  const [experienceId, setExperienceId] = useState('');
  const [releaseId, setReleaseId] = useState('');
  const [phase, setPhase] = useState(initialPhase || 'beta');
  const [releases, setReleases] = useState<ExperienceRelease[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfigId(initialConfigId || existingRelease?.configuration_id || '');
      setPhase(initialPhase || existingRelease?.phase || 'beta');
      setExperienceId(existingRelease?.experience_id || '');
      setReleaseId(existingRelease?.experience_release_id || '');
    }
  }, [isOpen, initialConfigId, initialPhase, existingRelease]);

  useEffect(() => {
    if (experienceId) {
      databaseService.listExperienceReleases(experienceId).then(setReleases);
    } else {
      setReleases([]);
      setReleaseId('');
    }
  }, [experienceId]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!configId || !experienceId || !releaseId || !phase) return;
    setSaving(true);
    const result = await databaseService.upsertConfigurationRelease({
      configuration_id: configId,
      experience_id: experienceId,
      experience_release_id: releaseId,
      phase,
    });
    setSaving(false);
    if (result) {
      onSave();
      onClose();
    }
  };

  const phases = ['production', 'beta', 'alpha', 'backup', 'short'];

  return (
    <div className="deployment-dialog-overlay" onClick={onClose}>
      <div className="deployment-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="deployment-dialog-title">
          {existingRelease ? 'Edit Release Assignment' : 'Assign Release to Configuration'}
        </div>

        <div className="deployment-dialog-field">
          <label className="deployment-dialog-label">Configuration</label>
          <select
            className="deployment-dialog-select"
            value={configId}
            onChange={(e) => setConfigId(e.target.value)}
          >
            <option value="">Select configuration...</option>
            {configurations.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="deployment-dialog-field">
          <label className="deployment-dialog-label">Experience</label>
          <select
            className="deployment-dialog-select"
            value={experienceId}
            onChange={(e) => {
              setExperienceId(e.target.value);
              setReleaseId('');
            }}
          >
            <option value="">Select experience...</option>
            {experiences.map((exp) => (
              <option key={exp.id} value={exp.id}>{exp.name}</option>
            ))}
          </select>
        </div>

        <div className="deployment-dialog-field">
          <label className="deployment-dialog-label">Release / Version</label>
          <select
            className="deployment-dialog-select"
            value={releaseId}
            onChange={(e) => setReleaseId(e.target.value)}
            disabled={!experienceId}
          >
            <option value="">Select release...</option>
            {releases.map((r) => (
              <option key={r.id} value={r.id}>
                v{r.version} — {r.path}{r.description ? ` (${r.description})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="deployment-dialog-field">
          <label className="deployment-dialog-label">Phase</label>
          <select
            className="deployment-dialog-select"
            value={phase}
            onChange={(e) => setPhase(e.target.value)}
          >
            {phases.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="deployment-dialog-actions">
          <button className="deployment-btn" onClick={onClose}>Cancel</button>
          <button
            className="deployment-btn deployment-btn-primary"
            onClick={handleSave}
            disabled={!configId || !experienceId || !releaseId || !phase || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
